import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type PendingTransaction } from '@/features/payouts/admin/PendingTransaction'
import type { AlertFactory } from '@/features/shell'
import { type PayoutReviewStore } from '@/features/payouts/admin/PayoutReviewStore'

/**
 * View model for cancelling a user transaction.
 */
export class CancelUserTransactionDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor transaction: PendingTransaction | null = null

  constructor(
    private readonly _payoutReviewStore: PayoutReviewStore,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * The short ID of the transaction being canceled.
   */
  @computed
  get transactionShortId() {
    return this.transaction?.shortId ?? ''
  }

  /**
   * Shows the dialog for cancelling the transaction.
   */
  @action.bound
  show(transaction: PendingTransaction) {
    this.cancelTransaction.reset()
    this.transaction = transaction
    this.modal.show()
  }

  /**
   * Cancels the transaction.
   */
  cancelTransaction = task.resolved(async () => {
    if (!this.transaction) {
      return
    }

    await this._payoutReviewStore.cancelUserTransaction({
      transaction: this.transaction,
    })
    this._newAlert()
      .withState('success')
      .saying('Success!', 'The transaction was canceled.')
      .show()
    runInAction(() => {
      this.modal.close()
    })
  })
}
